import {
  Avatar,
  Box,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";

import { Button, ThinDivider } from "../../../components";
import useDebounce from "../../../hooks/useDebounce";
import CopyLinkButton from "../ShareModal/CopyLinkButton";
import { ShareModalV2Props, ShareUser } from "./types";
import { UserAccessRow } from "./UserAccessRow";

/**
 * Helper function to get user display name
 */
const getUserDisplayName = (user: ShareUser): string => {
  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return user.fullName || fullName || user.email;
};

/**
 * ShareModalV2 - Matches exact Figma design for sharing
 */
export function ShareModalV2<TRole extends string = string>({
  isOpen,
  onClose,
  entityName,
  infoText,
  owner,
  ownerLabel = "Owner",
  shares,
  shareableUsers,
  availableRoles,
  defaultRole,
  specialAccessGroups = [],
  canShare = true,
  shareLoading = false,
  onShare,
  onRemoveShare,
  onChangeRole,
  searchPlaceholder = "Find user by name",
  onSearchChange,
  onDebouncedSearchChange,
  shareUrl,
}: ShareModalV2Props<TRole>) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setShowResults(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      onDebouncedSearchChange?.(debouncedSearchQuery);
    }
  }, [isOpen, debouncedSearchQuery, onDebouncedSearchChange]);

  // Filter users based on search - memoized for performance
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return [];

    const query = searchQuery.toLowerCase();
    const sharedUserIds = new Set(shares.map((s) => s.sharedTo.id));

    return shareableUsers
      .filter((user) => {
        // Don't show already shared users or owner
        if (sharedUserIds.has(user.id) || user.id === owner.id) return false;

        const fullName = getUserDisplayName(user).toLowerCase();
        return (
          fullName.includes(query) || user.email.toLowerCase().includes(query)
        );
      })
      .slice(0, 10);
  }, [shareableUsers, shares, owner.id, searchQuery]);

  const handleUserSelect = async (user: ShareUser, role: TRole) => {
    await onShare({
      userIds: [user.id],
      role,
    });
    setSearchQuery("");
    setShowResults(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxW="640px" borderRadius="24px">
        <ModalHeader
          pt="24px"
          pb="16px"
          px="24px"
          fontSize="24px"
          fontWeight="600"
          lineHeight="32px"
          color="gray.800"
        >
          Share {entityName}
        </ModalHeader>

        <ModalCloseButton
          top="20px"
          right="20px"
          color="gray.400"
          _hover={{ color: "gray.600" }}
        />

        <Box
          height="8px"
          background="var(--Gradient-Blue-Purple, linear-gradient(99deg, #0074DD -2.93%, #591EFF 58.85%, #9644FF 126.18%))"
        />

        <ModalBody p="24px">
          <VStack spacing="24px" align="stretch">
            {/* Info text */}
            {infoText && (
              <Text
                fontSize="14px"
                lineHeight="20px"
                color="gray.600"
                m="16px"
                fontWeight="400"
              >
                {infoText}
              </Text>
            )}

            {canShare && (
              <Box position="relative">
                <InputGroup size="md">
                  <InputLeftElement pointerEvents="none" h="40px">
                    <Icon as={FiSearch} color="gray.400" w="20px" h="20px" />
                  </InputLeftElement>
                  <Input
                    h="40px"
                    autoFocus
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      onSearchChange?.(e.target.value);
                      setShowResults(true);
                    }}
                    onFocus={() => setShowResults(true)}
                    borderColor="blue.500"
                    borderWidth="2px"
                    borderRadius="6px"
                    fontSize="14px"
                    aria-label={searchPlaceholder}
                    _hover={{ borderColor: "blue.600" }}
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "none",
                    }}
                    _placeholder={{ color: "gray.400" }}
                  />
                </InputGroup>

                {/* Search results dropdown - only shows when there are matching users */}
                {showResults && searchQuery && filteredUsers.length > 0 && (
                  <Box
                    position="absolute"
                    top="calc(100% + 4px)"
                    left={0}
                    right={0}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="4px"
                    boxShadow="0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)"
                    maxH="240px"
                    overflowY="auto"
                    zIndex={10}
                  >
                    {filteredUsers.map((user) => (
                      <Flex
                        key={user.id}
                        px="12px"
                        py="8px"
                        cursor="pointer"
                        _hover={{ bg: "gray.50" }}
                        onClick={() => {
                          const role = defaultRole || availableRoles[0]?.value;
                          if (role) {
                            handleUserSelect(user, role);
                          }
                        }}
                        align="center"
                      >
                        <Avatar
                          size="sm"
                          w="32px"
                          h="32px"
                          name={getUserDisplayName(user)}
                          src={user.profilePicUrl || undefined}
                          mr="12px"
                        />
                        <Box flex={1}>
                          <Text
                            fontSize="14px"
                            fontWeight="500"
                            lineHeight="20px"
                            color="gray.900"
                          >
                            {getUserDisplayName(user)}
                          </Text>
                          <Text
                            fontSize="12px"
                            lineHeight="16px"
                            color="gray.500"
                          >
                            {user.email}
                          </Text>
                        </Box>
                      </Flex>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <ThinDivider />

            <Box>
              <Text
                fontSize="14px"
                fontWeight="400"
                lineHeight="20px"
                color="gray.800"
                mb="16px"
              >
                Who has access
              </Text>

              <VStack spacing="0" align="stretch">
                <UserAccessRow
                  user={owner}
                  primaryText={getUserDisplayName(owner)}
                  roleLabel={ownerLabel}
                  testId="owner-access-row"
                />

                {shares.map((share) => (
                  <UserAccessRow
                    key={share.id}
                    user={share.sharedTo}
                    primaryText={getUserDisplayName(share.sharedTo)}
                    roleValue={share.role}
                    availableRoles={availableRoles}
                    onChangeRole={(role) => onChangeRole?.(share.id, role)}
                    onRemove={() => onRemoveShare(share.id)}
                  />
                ))}

                {specialAccessGroups.length > 0 && <ThinDivider my="8px" />}

                {specialAccessGroups.map((group) => (
                  <UserAccessRow
                    key={group.id}
                    avatarName={group.avatarIcon ? undefined : group.label}
                    avatarBg={group.avatarBg || "gray.500"}
                    avatarIcon={
                      group.avatarIcon ?? <Text fontSize="14px">👥</Text>
                    }
                    primaryText={group.label}
                    roleLabel={
                      availableRoles.find((r) => r.value === group.role)
                        ?.label || (group.role as unknown as string)
                    }
                  />
                ))}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter
          px="32px"
          py="20px"
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <HStack
            spacing="12px"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              variant="ghost"
              onClick={onClose}
              fontSize="16px"
              fontWeight="500"
              lineHeight="24px"
              h="40px"
              px="16px"
              py="8px"
              mr="32px"
              borderRadius="6px"
              color="blue.600"
              _hover={{ bg: "blue.50" }}
            >
              Cancel
            </Button>
            <HStack spacing="12px">
              <CopyLinkButton
                href={shareUrl || window.location.href}
                fontSize="16px"
                fontWeight="500"
                lineHeight="24px"
                h="40px"
                mr="16px"
                borderRadius="6px"
                color="blue.600"
                _hover={{ bg: "blue.100" }}
              />
              <Button
                colorScheme="blue"
                onClick={onClose}
                isLoading={shareLoading}
                fontSize="16px"
                fontWeight="500"
                lineHeight="24px"
                w="120px"
                h="40px"
                py="8px"
                px="24px"
                bg="blue.600"
                color="white"
                _hover={{ bg: "blue.700" }}
              >
                Done
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
